<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :centered="true"
    :width="760"
    wrapClassName="am30-rotate-angle-modal-wrap"
    :maskStyle="{ background: 'rgba(8, 8, 12, 0.82)' }"
    @cancel="closeModal"
  >
    <div class="am30-rotate-angle-modal">
      <div class="am30-rotate-angle-title">
        {{ $t("mouse.rotateAngle") }}
      </div>
      <div class="am30-rotate-angle-intro">
        {{ $t("mouse.rotateAngleTooltip") }}
      </div>
      <div class="am30-rotate-angle-preview">
        <div class="am30-rotate-angle-crosshair horizontal"></div>
        <div class="am30-rotate-angle-crosshair vertical"></div>
        <img
          class="am30-rotate-angle-mouse-shell"
          src="@/assets/images/mouse.png"
          alt="AM30 rotate angle preview"
          :style="rotateAnglePreviewStyle"
        />
      </div>
      <div class="am30-rotate-angle-slider">
        <div class="am30-rotate-angle-value">{{ previewRotateAngle }}</div>
        <a-slider
          class="slider rotate-angle-slider"
          :value="previewRotateAngle"
          :min="-90"
          :max="90"
          :tooltipVisible="false"
          @change="onRotateAngleSliderChange"
          @afterChange="onRotateAngleSliderAfterChange"
        />
        <div class="am30-rotate-angle-scale">
          <span class="scale-left">-90</span>
          <span class="scale-center">0</span>
          <span class="scale-right">90</span>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { mapState } from "vuex";

const ROTATE_ANGLE_PROTOCOL_DEBOUNCE_MS = 120;

function normalizeRotateAngleValue(value) {
  const angle = Number(value);
  if (Number.isNaN(angle)) {
    return 0;
  }
  return Math.max(-90, Math.min(90, angle));
}

function buildRotateAnglePayload(vm, nextAngle) {
  return {
    profile: Number(vm.param?.profile ?? 0),
    setSNEDORAngle: normalizeRotateAngleValue(nextAngle),
  };
}

function clearRotateAngleProtocolTimer(vm) {
  if (vm.rotateAngleProtocolTimer) {
    clearTimeout(vm.rotateAngleProtocolTimer);
    vm.rotateAngleProtocolTimer = null;
  }
}

function requestRotateAngleProtocol(vm, payload, { immediate = false } = {}) {
  clearRotateAngleProtocolTimer(vm);
  if (immediate) {
    vm.$store.dispatch("loader/setSNEDORAngle", payload);
    return;
  }

  vm.rotateAngleProtocolTimer = setTimeout(() => {
    vm.rotateAngleProtocolTimer = null;
    vm.$store.dispatch("loader/setSNEDORAngle", payload);
  }, ROTATE_ANGLE_PROTOCOL_DEBOUNCE_MS);
}

export default {
  name: "RotationModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rotateAngleProtocolTimer: null,
      liveRotateAngle: 0,
      isRotateAngleSliding: false,
    };
  },
  computed: {
    ...mapState("mouse", {
      param: "param",
    }),
    previewRotateAngle() {
      return normalizeRotateAngleValue(this.liveRotateAngle);
    },
    rotateAnglePreviewStyle() {
      return {
        transform: `translate(-50%, -50%) rotate(${this.previewRotateAngle}deg)`,
      };
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler() {
        this.syncPreviewRotateAngle(this.param?.setSNEDORAngle);
      },
    },
    "param.setSNEDORAngle"(value) {
      if (this.isRotateAngleSliding) {
        return;
      }
      this.syncPreviewRotateAngle(value);
    },
  },
  methods: {
    syncPreviewRotateAngle(value) {
      this.liveRotateAngle = normalizeRotateAngleValue(value);
    },
    closeModal() {
      this.isRotateAngleSliding = false;
      this.syncPreviewRotateAngle(this.param?.setSNEDORAngle);
      this.$emit("update:visible", false);
    },
    onRotateAngleSliderChange(value) {
      const nextAngle = normalizeRotateAngleValue(value);
      const payload = buildRotateAnglePayload(this, nextAngle);
      this.isRotateAngleSliding = true;
      this.syncPreviewRotateAngle(nextAngle);
      this.$store.dispatch("mouse/setParam", {
        ...this.param,
        setSNEDORAngle: nextAngle,
      });
      requestRotateAngleProtocol(this, payload);
    },
    onRotateAngleSliderAfterChange(value) {
      const nextAngle = normalizeRotateAngleValue(value);
      const payload = buildRotateAnglePayload(this, nextAngle);
      this.isRotateAngleSliding = false;
      this.syncPreviewRotateAngle(nextAngle);
      this.$store.dispatch("mouse/setParam", {
        ...this.param,
        setSNEDORAngle: nextAngle,
      });
      requestRotateAngleProtocol(this, payload, { immediate: true });
    },
  },
  beforeDestroy() {
    clearRotateAngleProtocolTimer(this);
  },
};
</script>

<style lang="scss" scoped>
.am30-rotate-angle-modal {
  color: #ffffff;
  padding: 18px 12px 8px;

  .am30-rotate-angle-title {
    font-size: 28px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 18px;
  }

  .am30-rotate-angle-intro {
    width: 100%;
    font-size: 16px;
    line-height: 1.75;
    color: #d4d4de;
    margin-bottom: 28px;
  }

  .am30-rotate-angle-preview {
    position: relative;
    width: 320px;
    height: 230px;
    margin: 0 auto 30px;
  }

  .am30-rotate-angle-crosshair {
    position: absolute;
    background: rgba(255, 255, 255, 0.2);

    &.horizontal {
      left: 26px;
      right: 26px;
      top: 50%;
      height: 1px;
      transform: translateY(-50%);
    }

    &.vertical {
      top: 10px;
      bottom: 10px;
      left: 50%;
      width: 1px;
      transform: translateX(-50%);
    }
  }

  .am30-rotate-angle-mouse-shell {
    position: absolute;
    top: 50%;
    left: 50%;
    width: auto;
    height: 66%;
    max-width: 100%;
    transition: transform 0.12s ease-out;
    transform-origin: center;
    will-change: transform;
    pointer-events: none;
    user-select: none;
  }

  .am30-rotate-angle-slider {
    width: 100%;
    max-width: 620px;
    margin: 0 auto;
  }

  .am30-rotate-angle-value {
    width: 44px;
    height: 32px;
    line-height: 32px;
    margin: 0 auto 16px;
    text-align: center;
    font-size: 16px;
    color: $primary-color;
    border: 1px solid rgba(191, 148, 255, 0.65);
    border-radius: 6px;
    background: rgba(22, 16, 30, 0.92);
  }

  .am30-rotate-angle-scale {
    position: relative;
    height: 24px;
    margin-top: 6px;
    font-size: 16px;
    color: #cfcfe1;

    span {
      position: absolute;
      top: 0;
      transform: translateX(-50%);
    }

    .scale-left {
      left: 0;
      transform: translateX(0);
    }

    .scale-center {
      left: 50%;
    }

    .scale-right {
      left: 100%;
    }
  }
}
</style>

<style lang="scss">
.am30-rotate-angle-modal-wrap {
  .ant-modal-content {
    background: #0c0c10;
    border: 1px solid rgba(191, 148, 255, 0.28);
    border-radius: 16px;
    box-shadow: 0 24px 80px rgba(73, 41, 132, 0.42);
  }

  .ant-modal-close {
    color: #ffffff;
  }

  .ant-modal-body {
    padding: 22px 26px 28px;
  }

  .rotate-angle-slider {
    padding: 4px 0;

    .ant-slider-rail {
      height: 4px;
      background-color: #4c3a61 !important;
    }

    .ant-slider-track {
      height: 4px;
      background-color: $primary-color !important;
    }

    .ant-slider-step {
      height: 4px;
    }

    .ant-slider-handle {
      width: 20px;
      height: 20px;
      margin-top: -7px;
      background-color: $primary-color !important;
      border: 0;
      box-shadow: none;
    }
  }
}
</style>
